/**
 * Permission Constants
 * Module names, permission levels, and actions for the application
 *
 * All permissions are resolved from roles (user_role_assignments + role_permissions).
 */

// Permission Levels (used by ProtectedRoute and Sidebar for route definitions)
export const PERMISSION_LEVELS = {
  NONE: 'none',
  READ: 'read',
  WRITE: 'write',
  ADMIN: 'admin',
};

// Granular Actions (role-based system)
export const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  APPROVE: 'approve',
};

// Action labels for UI display
export const ACTION_LABELS = {
  read: 'Read',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  approve: 'Approve',
};

// Modules
export const MODULES = {
  CAPITAL_MANAGEMENT: 'capital_management',
  CONTRACT_MANAGEMENT: 'contract_management',
  STAKEHOLDER_MANAGEMENT: 'stakeholder_management',
  REFERENCE_DATA: 'reference_data',
  AMORTIZATION_SCHEDULES: 'amortization_schedules',
  REPORTS: 'reports',
  ADMIN_SETTINGS: 'admin_settings',
  USER_MANAGEMENT: 'user_management',
  TENANT_MANAGEMENT: 'tenant_management',
  PAYMENTS: 'payments',
  AUDIT_TRAIL: 'audit_trail',
  CLIENT_PORTAL: 'client_portal',
  WORKFLOW_DOCUMENT_REQUIREMENTS: 'workflow_document_requirements',
};

// Module labels for UI display
export const MODULE_LABELS = {
  capital_management: 'Capital Management',
  contract_management: 'Contract Management',
  stakeholder_management: 'Stakeholders',
  reference_data: 'Reference Data',
  amortization_schedules: 'Amortization',
  reports: 'Reports',
  admin_settings: 'Admin Settings',
  user_management: 'User Management',
  tenant_management: 'Tenant Management',
  payments: 'Payments',
  audit_trail: 'Audit Trail',
  client_portal: 'Client Portal',
  workflow_document_requirements: 'Workflow Document Requirements',
};

// Module categories for grouping in UI
export const MODULE_CATEGORIES = {
  business: [
    'capital_management',
    'contract_management',
    'payments',
    'stakeholder_management',
    'reports',
    'amortization_schedules',
  ],
  admin: [
    'reference_data',
    'admin_settings',
    'user_management',
    'tenant_management',
    'audit_trail',
    'client_portal',
    'workflow_document_requirements',
  ],
};

/**
 * Check if user has a specific action on a module
 * @param {Object} permissions - Action-based permissions { module: { action: bool } }
 * @param {string} module - Module name
 * @param {string} action - Required action (read, create, update, delete, approve)
 * @returns {boolean} Has the action
 */
export const hasAction = (permissions, module, action) => {
  if (!permissions || !module || !action) return false;

  const modulePerms = permissions[module];

  if (modulePerms && typeof modulePerms === 'object') {
    return modulePerms[action] === true;
  }

  return false;
};

/**
 * Module Tree Structure
 * 3-level hierarchy: Module → Category → Feature
 * Used by the tenant module configuration tree UI.
 * Adding/removing nodes here automatically updates the UI.
 */
export const MODULE_TREE = {
  capital_management: {
    label: 'Capital Management',
    children: {
      dashboard: { label: 'Dashboard' },
    },
  },
  contract_management: {
    label: 'Contract Management',
    children: {
      contracts: {
        label: 'Contracts',
        children: {
          listing: { label: 'Contract Listing' },
          creation: { label: 'Contract Creation' },
          editing: { label: 'Contract Editing' },
        },
      },
      workflows: {
        label: 'Workflows',
        children: {
          approvals: { label: 'Approval Workflows' },
          rate_changes: { label: 'Rate Change Analysis' },
          schedule_amendments: { label: 'Schedule Amendments' },
        },
      },
      documents: {
        label: 'Documents',
        children: {
          upload: { label: 'Document Upload' },
          review: { label: 'Document Review' },
        },
      },
    },
  },
  payments: {
    label: 'Payments',
    children: {
      matching: { label: 'Payment Matching' },
      capital_receipts: { label: 'Capital Receipts' },
      withholding_tax: { label: 'Withholding Tax' },
    },
  },
  stakeholder_management: {
    label: 'Stakeholders',
    children: {
      investors: {
        label: 'Investor Management',
        children: {
          basic_information: { label: 'Basic Information' },
          identity_classification: { label: 'Identity & Classification' },
          banking_details: { label: 'Banking Details' },
          contact_details: { label: 'Contact Details' },
          kyc_aml: { label: 'KYC / AML Compliance' },
          relationship_management: { label: 'Relationship Management' },
          wht_override: { label: 'WHT Override' },
          commission_override: { label: 'Commission Override' },
        },
      },
      brokers: {
        label: 'Broker Management',
        children: {
          basic_information: { label: 'Basic Information' },
          contact_info: { label: 'Contact Information' },
          commission_structure: { label: 'Commission Structure' },
          regulatory_licensing: { label: 'Regulatory & Licensing' },
          relationship: { label: 'Relationship' },
        },
      },
      collateral: {
        label: 'Collateral Providers',
        children: {
          basic_information: { label: 'Basic Information' },
          contact_info: { label: 'Contact Information' },
          credit_capacity: { label: 'Credit & Capacity' },
          regulatory: { label: 'Regulatory' },
          capability: { label: 'Capability' },
        },
      },
    },
  },
  reports: {
    label: 'Reports',
    children: {
      contract_register: { label: 'Contract Register' },
      payment_schedule: { label: 'Payment Schedule' },
      investor_summary: { label: 'Investor Summary' },
      audit_trail: { label: 'Audit Trail' },
    },
  },
  reference_data: {
    label: 'Reference Data',
    children: {
      countries: { label: 'Countries & Currencies' },
      frequencies: { label: 'Frequencies' },
      investment_types: { label: 'Investment Types' },
      interest_rates: { label: 'Interest Rates' },
      fx_rates: { label: 'FX Rates' },
      placement_fees: { label: 'Placement Fees' },
      collateral_types: { label: 'Collateral Types' },
      narrations: { label: 'Narrations' },
      calculation_profiles: { label: 'Calculation Profiles' },
    },
  },
  user_management: {
    label: 'User Management',
    children: {
      users: { label: 'User CRUD' },
      pending_approvals: { label: 'Pending User Approvals' },
      roles: { label: 'Role Management' },
    },
  },
  admin_settings: {
    label: 'Admin Settings',
    children: {
      email: {
        label: 'Email Notifications',
        children: {
          templates: { label: 'Email Templates' },
          recipients: { label: 'Recipient Management' },
          audit: { label: 'Email Audit' },
        },
      },
      pdf: { label: 'PDF Templates' },
      system: {
        label: 'System',
        children: {
          api_settings: { label: 'API Settings' },
          service_status: { label: 'Service Status' },
          document_types: { label: 'Document Types' },
        },
      },
    },
  },
  tenant_management: {
    label: 'Tenant Management',
    children: {
      provisioning: { label: 'Tenant Provisioning' },
      configuration: { label: 'Tenant Configuration' },
    },
  },
  amortization_schedules: {
    label: 'Amortization Schedules',
    children: {
      generation: { label: 'Schedule Generation' },
      versioning: { label: 'Version History' },
      calculation_audit: { label: 'Calculation Audit Trail' },
    },
  },
  client_portal: {
    label: 'Client Portal',
    children: {
      investor_dashboard: { label: 'Investor Dashboard' },
      contract_viewing: { label: 'Contract Viewing' },
      document_access: { label: 'Document Access' },
    },
  },
  audit_trail: {
    label: 'Audit Trail',
    children: {
      operation_logs: { label: 'Operation Logs' },
      calculation_audit: { label: 'Calculation Transparency' },
    },
  },
  workflow_document_requirements: {
    label: 'Workflow Document Requirements',
    children: {
      page: { label: 'Mandatory Document Toggle' },
    },
  },
};

/**
 * Get all modules as an array (for iteration)
 */
export const getAllModules = () => Object.values(MODULES);

/**
 * Get all actions as an array (for iteration)
 */
export const getAllActions = () => Object.values(ACTIONS);
